import { type ComponentProps, type Dispatch, type FC, type SetStateAction } from "react";
import { AlbumCassettes } from "@clientFrontend/features/publication/components/AlbumCassettes";
import { Button } from "@root/shared/components/Button";
import { ClientImageLibraryModal } from "@clientFrontend/features/publication/components/ClientImageLibraryModal";
import { PlanAndImageInfoFooterContent } from "@clientFrontend/features/publication/components/ClientImageLibraryModal/uiParts/PlanAndImageInfoFooterContent";
import { type WorkContentAlbum } from "@clientFrontend/features/publication/type/type";
import { Icon } from "@root/shared/components/Icon";
import {
  type ClientImage,
  type PlanAndImageInfoProps,
} from "@clientFrontend/features/publication/components/ClientImageLibraryModal/type";
import { DeletedAlbumParamsBuilder } from "@clientFrontend/features/publication/components/SelectedWorkAlbumImage/uiParts/DeletedAlbumParamsBuilder";
import { type OptionProps } from "@root/shared/components/SelectBox";
import { type ApiUrls } from "./type";
import * as styles from "./styles.css";

type Props = {
  buttonProps: ComponentProps<typeof Button>;
  initialWorkContentAlbums: WorkContentAlbum[];
  selectedWorkContentAlbums: WorkContentAlbum[];
  showModal: boolean;
  apiUrls: ApiUrls;
  errorMessages: string[];
  planAndImageInfoProps: PlanAndImageInfoProps;
  albumCategoryOptions: OptionProps[];
  clientImages: ClientImage[];
  fetchClientImages: () => void;
  setClientImages: Dispatch<SetStateAction<ClientImage[]>>;
  handleConfirmAlbumImages: (images: ClientImage[]) => void;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  handleChangeAlbumSortNo: (currentSortNo: number, swapSortNo: number) => void;
  handleChangeAlbumCategory: (albumCategoryId: number, clientImageId: number) => void;
};

export const Presentation: FC<Props> = ({
  buttonProps,
  initialWorkContentAlbums,
  selectedWorkContentAlbums,
  showModal,
  apiUrls,
  errorMessages,
  planAndImageInfoProps,
  albumCategoryOptions,
  clientImages,
  fetchClientImages,
  setClientImages,
  handleConfirmAlbumImages,
  setShowModal,
  handleChangeAlbumSortNo,
  handleChangeAlbumCategory,
}) => {
  return (
    <div className={styles.wrapper}>
      {errorMessages.map((message) => (
        <div key={message} className={styles.errorText}>
          <Icon name="attention" />
          <span className={styles.errorTextSpace}>{message}</span>
        </div>
      ))}
      <div className={styles.openModalButtonWrapper}>
        <p className={styles.openModalButtonExplanation}>
          最小枚数：3枚
          <br />
          最大枚数：{planAndImageInfoProps.userDisplayableMaxWorkContentAlbumCountText}
          （※上限を超えて設定された画像は、求人には掲載されません。）
        </p>
        <Button
          {...buttonProps}
          theme="secondary"
          className={styles.openModalButton}
          leftIcon={<Icon name="picture" />}
        >
          画像の設定
        </Button>
      </div>
      {showModal && (
        <ClientImageLibraryModal
          mode="multiple"
          maxCount={planAndImageInfoProps.maxWorkContentAlbumCount}
          clientImageUrl={apiUrls.clientImageUrl}
          initialSelectedImages={selectedWorkContentAlbums.map((album) => ({
            id: album.clientImageId,
            imageUrl: album.imageUrl,
            filename: album.fileName,
          }))}
          clientImages={clientImages}
          fetchClientImages={fetchClientImages}
          setClientImages={setClientImages}
          onConfirm={handleConfirmAlbumImages}
          onClose={() => setShowModal(false)}
          renderFooter={({ selectedCount, onConfirm }) => (
            <PlanAndImageInfoFooterContent
              planAndImageInfoProps={{
                ...planAndImageInfoProps,
                selectedImagesCount: selectedCount,
              }}
              buttonProps={{ onClick: onConfirm }}
            />
          )}
        />
      )}

      {/* サーバーに送るためのparamsの値はAlbumCassettesの中で定義 */}
      <AlbumCassettes
        selectedWorkContentAlbums={selectedWorkContentAlbums}
        albumCategoryOptions={albumCategoryOptions}
        handleChangeAlbumSortNo={handleChangeAlbumSortNo}
        handleChangeAlbumCategory={handleChangeAlbumCategory}
      />

      {/* サーバーに送るための(DELETE)用のparamsの値はDeletedAlbumParamsBuilderの中で定義 */}
      <DeletedAlbumParamsBuilder
        initialWorkContentAlbums={initialWorkContentAlbums}
        selectedWorkContentAlbums={selectedWorkContentAlbums}
      />
    </div>
  );
};
